import { useLocalStorageState, useMemoizedFn } from 'ahooks'
import md5 from 'md5'

import { signSecretHeader } from './helpers/signSecretHeader'
import { useTrackCommonParams } from './trackCommonParams'
import { useTrackCore } from './core'

import type { TrackEventParams } from './helpers/trackEmitter'
import type { CommonParamsResult } from './trackCommonParams'
import type { TrackEventParamsInput } from '@loc/server/interface/track/trackEventParams.common'
import type { $Enums } from '@prisma/client'

import { isTestEnvironmentVisitor } from '@/helpers/isTestEnvironmentVisitor'
import { api } from '@/utils/api'

export interface IEcommerce {
  currency: string
  value: number
  appName: $Enums.SubLogAppName
  payment_platform?: string
  transaction_id?: string
  origin_url?: string
  coupon?: string
  renewal?: number | string
  /** 剩余额度 */
  remaining_usage?: Record<string, string | number>
  origin_referrer?: string
  items: {
    item_id: string
    item_name: string
    /** 数量 */
    quantity: number
    price: number
    /** 产品所属名称即appName */
    item_category: $Enums.SubLogAppName
    /** 周期 Annually | Monthly */
    item_category2: 'Annually' | 'Monthly'
    /** customer id */
    item_category5?: string
  }[]
}

export interface TrackCustomEventProps {
  commonParamsLatest: CommonParamsResult
}

export const UserSignUpSessionStorageName = 'user-sign-up'

export function useAnalyticsData() {
  const getCommonParamsLatest = useTrackCommonParams()
  const { setSignSecretHeader, removeSignSecretHeader } = signSecretHeader()

  const { mutateAsync } = api.event.tracking.useMutation({
    onSettled() {
      removeSignSecretHeader()
    },
    trpc: {
      abortOnUnmount: false,
      context: {
        skipError: true,
        skipBatch: true,
      },
    },
  })

  const eventTracking = useMemoizedFn(async (params: TrackEventParamsInput) => {
    if (process.env.NEXT_PUBLIC_APP_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('trackCustomEvent', params)
    }
    if (isTestEnvironmentVisitor()) {
      return { status: 'success' as const }
    }
    // 添加签名
    const lastParam = params[params.length - 1]
    const signStr = `name=${lastParam.name}&commTimestamp=${lastParam.commTimestamp}&commUserGroup=${lastParam.commUserGroup}`
    const secret = md5(signStr)
    setSignSecretHeader(secret)
    const res = await mutateAsync(params)
    return res
  })

  const { trackCoreEvent } = useTrackCore({ eventTracking })

  /** 统一事件入口 */
  const trackCustomEvent = useMemoizedFn(async (props: TrackEventParams) => {
    const { latestUserId, latestEmail, ...rest } = props

    const commonParamsLatest = await getCommonParamsLatest.current({
      latestUserId,
      latestEmail,
    })

    await trackCoreEvent({ ...rest, commonParamsLatest })
  })

  // 注册状态标记（业务用）
  const [userIsSignUp, setUserIsSignUp] = useLocalStorageState<
    string | undefined
  >(UserSignUpSessionStorageName, {
    defaultValue: '',
  })

  const onChangeUserIsSignUp = useMemoizedFn((value?: string) => {
    setUserIsSignUp(value)
  })

  return {
    trackCustomEvent,
    userIsSignUp,
    onChangeUserIsSignUp,
  }
}
